import {
  Button,
  CheckboxGroup,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Radio,
  TagLabel,
  Tag,
  useCheckboxGroup,
  useRadioGroup,
  VStack,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  HStack,
  Link,
} from "@chakra-ui/react";
import Footer from "_components/Footer";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  FaArrowLeft,
  FaArrowRight,
  FaRegMoneyBillAlt,
  FaUsers,
  FaFacebookF,
  FaTwitter,
} from "react-icons/fa";
import { format } from "date-fns";
import {
  formatAsCurrency,
  formatContractJobAdDuration,
} from "helpers/utils/funtions";
import { logEvent } from "helpers/analytics";
import { useLazyQuery } from "@apollo/client";
import { GET_EMPLOYER_JOB_ADS } from "./queries";
import Loading from "_components/Loading";
import JobAdSummaryCard from "_components/jobs/JobAdSummaryCard";
import RequestFailed from "_components/RequestFailed";
import { GrLocation } from "react-icons/gr";
import { RiBuildingLine } from "react-icons/ri";
import { MdOutlineBusinessCenter } from "react-icons/md";
import { IoCheckmarkCircle } from "react-icons/io5";
import { scrollToTop } from "lib/utils";
import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from "@chakra-ui/icons";
import {
  EmploymentType,
  GetEmployerJobAdsQueryVariables,
} from "../../__generated__/graphql";
import { Tags, Share } from "lucide-react";
import { RxCounterClockwiseClock } from "react-icons/rx";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useEmbed } from "../../hooks/useEmbed";
import { useEmbedResize } from "../../hooks/useEmbedResize";

export const employmentTypeToText = (type: string) => {
  switch (type) {
    case "FULL_TIME":
      return "Full Time";
    case "PART_TIME":
      return "Part Time";
    case "CONTRACT":
      return "Contract";
    default:
      return "Unknown";
  }
};

const dateFilterOptions = [
  {
    value: "ALL_TIME",
    label: "All time",
  },
  {
    value: "YESTERDAY",
    label: "Yesterday",
  },
  {
    value: "LAST_WEEK",
    label: "Last week",
  },
  {
    value: "LAST_MONTH",
    label: "Last month",
  },
];

const employmentTypeOptions = [
  {
    value: "FULL_TIME",
    label: "Full Time",
  },
  {
    value: "CONTRACT",
    label: "Contract",
  },
];

const defaultFilters: {
  dateFilter: GetEmployerJobAdsQueryVariables["dateFilter"];
  employmentTypes: EmploymentType[];
} = {
  dateFilter: null,
  employmentTypes: [],
};

export function JobsLandingPage() {
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [selectedJobId, setSelectedJobId] = React.useState<string | null>(null);
  const [showShareModal, setShowShareModal] = React.useState(false);
  const [shareJobId, setShareJobId] = React.useState<string | null>(null);

  const { getAccessTokenSilently, isLoading: isLoadingAuth0 } = useAuth0();
  const { isEmbedded } = useEmbed();
  useEmbedResize();

  const history = useHistory();
  const { pathname } = useLocation();

  const [dateFilter, setDateFilter] =
    React.useState<GetEmployerJobAdsQueryVariables["dateFilter"]>(null);

  const [employmentTypesFilter, setEmploymentTypesFilter] = React.useState<
    Array<EmploymentType>
  >([]);

  const [dateFiltersOpen, setDateFiltersOpen] = React.useState(false);
  const [employmentTypesOpen, setEmploymentTypesOpen] = React.useState(false);

  const { getRadioProps: getDateFilterRadioProps } = useRadioGroup({
    defaultValue: dateFilter || undefined,
    value: dateFilter || undefined,
    onChange: (value) => {
      setDateFilter(value as GetEmployerJobAdsQueryVariables["dateFilter"]);
    },
  });

  const {
    getCheckboxProps: getEmploymentTypeCheckboxProps,
    value: employmentTypesValue,
  } = useCheckboxGroup({
    defaultValue: employmentTypesFilter,
    value: employmentTypesFilter,
    onChange: (values) => {
      setEmploymentTypesFilter(values as Array<EmploymentType>);
    },
  });

  const jobsPerPage = 6;

  const [getEmployerJobAds, { loading, error, data }] =
    useLazyQuery(GET_EMPLOYER_JOB_ADS);

  useEffect(() => {
    getEmployerJobAds({ variables: defaultFilters });
    getAccessTokenSilently().catch((error) => {
      console.error(error);
    });
  }, []);

  useEffect(() => {
    const jobApplicationRedirect = localStorage.getItem(
      "jobApplicationRedirect"
    );
    if (jobApplicationRedirect) {
      localStorage.removeItem("jobApplicationRedirect");
      history.push(jobApplicationRedirect);
    }
  }, [dateFilter]);

  const jobs = data?.getEmployerJobAds;

  const totalPages = (jobs && Math.ceil(jobs.length / jobsPerPage)) || 0;

  const paginatedJobs =
    (!loading &&
      jobs &&
      jobs.slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage)) ||
    [];

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  useEffect(() => {
    scrollToTop();
  }, [currentPage]);

  if (loading || isLoadingAuth0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loading />
      </div>
    );
  }

  if (error) {
    return <RequestFailed text={error.message} />;
  }

  const selectedJob = jobs?.find((job) => job.id === selectedJobId);
  const shareJob = jobs?.find((job) => job.id === shareJobId);

  const handleJobShare = (jobId: string, jobTitle: string) => {
    const url = `${window.location.origin}/jobs/${jobId}`;

    logEvent("job_share_clicked", {
      job_id: jobId,
      job_title: jobTitle,
    });

    if (navigator.share) {
      navigator
        .share({
          title: `${jobTitle} | LaborHack Jobs`,
          text: `Check out this job opportunity: ${jobTitle}`,
          url,
        })
        .then(() => {
          logEvent("job_shared", {
            job_id: jobId,
            job_title: jobTitle,
          });
        })
        .catch((error) => {
          if (error.name === "AbortError") {
            return;
          }

          logEvent("job_share_failed", {
            job_id: jobId,
            job_title: jobTitle,
            error: error.message,
          });

          setShareJobId(jobId);
          setShowShareModal(true);
        });
    } else {
      setShareJobId(jobId);
      setShowShareModal(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>LaborHack Jobs</title>
      </Helmet>
      <Modal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        isCentered
        closeOnOverlayClick
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Share Job</ModalHeader>
          <VStack pb={8} px={6} alignItems="start">
            <HStack>
              <Link
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  `Check out this job opportunity: ${
                    shareJob?.title || "Job"
                  } on LaborHack`
                )}&url=${encodeURIComponent(
                  `${window.location.origin}/jobs/${shareJobId}`
                )}`}
                isExternal
              >
                <Button
                  rightIcon={<FaTwitter />}
                  colorScheme="twitter"
                  variant="solid"
                  onClick={() =>
                    logEvent("job_twitter_share_clicked", {
                      job_id: shareJobId,
                      job_title: shareJob?.title,
                    })
                  }
                >
                  Tweet
                </Button>
              </Link>
              <Link
                href={
                  "https://www.facebook.com/sharer/sharer.php?u=" +
                  encodeURIComponent(
                    `${window.location.origin}/jobs/${shareJobId}`
                  )
                }
                isExternal
              >
                <Button
                  rightIcon={<FaFacebookF />}
                  colorScheme="facebook"
                  variant="solid"
                  onClick={() =>
                    logEvent("job_facebook_share_clicked", {
                      job_id: shareJobId,
                      job_title: shareJob?.title,
                    })
                  }
                >
                  Post to Facebook
                </Button>
              </Link>
            </HStack>
          </VStack>
        </ModalContent>
      </Modal>
      <VStack spacing={0} maxW={"99vw"}>
        <section
          id="jobs"
          className="px-3 lg:px-20 py-8 lg:py-16 w-full flex flex-col gap-4 lg:gap-6"
        >
          <div
            className={`w-full ${
              !selectedJob ? "lg:w-[70%]" : "lg:w-full"
            } border-[#ACACAC] border-y-[1px] flex flex-col lg:flex-row gap-5 lg:gap-8 lg:items-center py-4`}
          >
            <div>
              <p className="text-[#6E6E6E] text-[16px] lg:text-[24px]">
                Filters
              </p>
            </div>
            <div className="flex gap-4">
              <Menu
                matchWidth={false}
                isOpen={dateFiltersOpen}
                onClose={() => setDateFiltersOpen(false)}
                closeOnSelect={false}
              >
                {({ isOpen, onClose }) => (
                  <>
                    <MenuButton
                      as={Button}
                      rightIcon={
                        isOpen ? (
                          <ChevronUpIcon w={6} h={6} />
                        ) : (
                          <ChevronDownIcon w={6} h={6} />
                        )
                      }
                      w={{
                        base: "45%",
                        lg: "180px",
                      }}
                      h={{
                        base: "40px",
                        lg: "56px",
                      }}
                      border={"1px solid #858585"}
                      fontSize={{
                        base: "15px",
                        lg: "20px",
                      }}
                      textColor={"#212121"}
                      backgroundColor={"#FEFEFE"}
                      onClick={() => {
                        setDateFiltersOpen(!isOpen);
                      }}
                    >
                      Date posted
                    </MenuButton>
                    <MenuList
                      rounded={0}
                      w={{
                        base: "260px",
                        lg: "547px",
                      }}
                    >
                      <>
                        <div className="flex justify-between items-center px-3 pb-3 border-b-[1px] border-[#D8D8D8]">
                          <p className="text-[#464646] text-[16px] lg:text-[22px]">
                            Date posted
                          </p>
                          <IconButton
                            aria-label="close"
                            variant={"outline"}
                            rounded={"100%"}
                            border={0}
                            color={"#212121"}
                            onClick={() => onClose()}
                            icon={<Icon as={CloseIcon} w={3} h={3} />}
                          />
                        </div>
                        <div className="py-4 border-b-[1px] border-[#D8D8D8]">
                          {dateFilterOptions.map((option) => (
                            <MenuItem key={option.value}>
                              <Radio
                                {...(getDateFilterRadioProps({
                                  value: option.value,
                                }) as any)}
                                isChecked={dateFilter === option.value}
                                size={"lg"}
                                fontSize={{
                                  base: "16px",
                                  lg: "22px",
                                }}
                              >
                                {option.label}
                              </Radio>
                            </MenuItem>
                          ))}
                        </div>
                      </>
                      <div className="flex gap-6 justify-end px-3 py-4">
                        <Button
                          colorScheme="flatteredFlamingo"
                          variant={"unstyled"}
                          textDecoration={"underline"}
                          h={{
                            base: "38px",
                            lg: "49px",
                          }}
                          fontSize={{
                            base: "14px",
                            lg: "16px",
                          }}
                          onClick={() => {
                            setDateFilter(defaultFilters.dateFilter);
                            getEmployerJobAds({
                              variables: {
                                dateFilter: defaultFilters.dateFilter,
                                employmentTypes: employmentTypesFilter,
                              },
                            });
                            setDateFiltersOpen(false);
                          }}
                        >
                          Clear filter
                        </Button>
                        <Button
                          colorScheme="flatteredFlamingo"
                          h={{
                            base: "38px",
                            lg: "49px",
                          }}
                          fontSize={{
                            base: "16px",
                            lg: "20px",
                          }}
                          onClick={() => {
                            getEmployerJobAds({
                              variables: {
                                dateFilter,
                                employmentTypes: employmentTypesFilter,
                              },
                            }).then(() => {
                              setDateFiltersOpen(false);
                            });
                          }}
                        >
                          Apply filter
                        </Button>
                      </div>
                    </MenuList>
                  </>
                )}
              </Menu>

              <Menu
                isOpen={employmentTypesOpen}
                onClose={() => setEmploymentTypesOpen(false)}
                closeOnSelect={false}
              >
                {({ isOpen, onClose }) => (
                  <>
                    <MenuButton
                      as={Button}
                      rightIcon={
                        isOpen ? (
                          <ChevronUpIcon w={6} h={6} />
                        ) : (
                          <ChevronDownIcon w={6} h={6} />
                        )
                      }
                      w={{
                        base: "38%",
                        lg: "140px",
                      }}
                      h={{
                        base: "40px",
                        lg: "56px",
                      }}
                      border={"1px solid #858585"}
                      fontSize={{
                        base: "15px",
                        lg: "20px",
                      }}
                      textColor={"#212121"}
                      backgroundColor={"#FEFEFE"}
                      onClick={() => {
                        setEmploymentTypesOpen(!isOpen);
                      }}
                    >
                      Job type
                    </MenuButton>
                    <MenuList
                      rounded={0}
                      w={{
                        base: "260px",
                        lg: "547px",
                      }}
                    >
                      <>
                        <div className="flex justify-between items-center px-3 pb-3 border-b-[1px] border-[#D8D8D8]">
                          <p className="text-[#464646] text-[16px] lg:text-[22px]">
                            Job type
                          </p>
                          <IconButton
                            aria-label="close"
                            variant={"outline"}
                            rounded={"100%"}
                            border={0}
                            color={"#212121"}
                            onClick={() => onClose()}
                            icon={<Icon as={CloseIcon} w={3} h={3} />}
                          />
                        </div>
                        <div className="py-4 border-b-[1px] border-[#D8D8D8]">
                          <CheckboxGroup value={employmentTypesValue}>
                            {employmentTypeOptions.map((option) => (
                              <MenuItem key={option.value}>
                                <Radio
                                  {...getEmploymentTypeCheckboxProps({
                                    value: option.value,
                                  })}
                                  isChecked={employmentTypesFilter.includes(
                                    option.value as EmploymentType
                                  )}
                                  size={"lg"}
                                  fontSize={{
                                    base: "16px",
                                    lg: "22px",
                                  }}
                                >
                                  {option.label}
                                </Radio>
                              </MenuItem>
                            ))}
                          </CheckboxGroup>
                        </div>
                      </>
                      <div className="flex gap-6 justify-end px-3 py-4">
                        <Button
                          colorScheme="flatteredFlamingo"
                          variant={"unstyled"}
                          textDecoration={"underline"}
                          h={{
                            base: "38px",
                            lg: "49px",
                          }}
                          fontSize={{
                            base: "14px",
                            lg: "16px",
                          }}
                          onClick={() => {
                            setEmploymentTypesFilter(
                              defaultFilters.employmentTypes
                            );
                            getEmployerJobAds({
                              variables: {
                                dateFilter,
                                employmentTypes: defaultFilters.employmentTypes,
                              },
                            }).then(() => {
                              setDateFiltersOpen(false);
                            });
                          }}
                        >
                          Clear filter
                        </Button>
                        <Button
                          colorScheme="flatteredFlamingo"
                          h={{
                            base: "38px",
                            lg: "49px",
                          }}
                          fontSize={{
                            base: "16px",
                            lg: "20px",
                          }}
                          onClick={() => {
                            getEmployerJobAds({
                              variables: {
                                dateFilter,
                                employmentTypes: employmentTypesFilter,
                              },
                            }).then(() => {
                              setEmploymentTypesOpen(false);
                            });
                          }}
                        >
                          Apply filter
                        </Button>
                      </div>
                    </MenuList>
                  </>
                )}
              </Menu>
            </div>
          </div>
          <div className="w-full">
            <h2
              className="text-[20px] lg:text-[32px] font-bold text-[#122A38]"
              id="jobs-header"
            >
              Jobs
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 mt-4">
              <div
                className={`flex flex-col gap-4 w-full h-full ${
                  !selectedJob ? "col-span-7" : "hidden lg:block lg:col-span-5"
                }`}
              >
                {!loading && paginatedJobs.length > 0 ? (
                  paginatedJobs.map((job, idx) => (
                    <div
                      className={`w-full ${idx !== 0 ? "mt-3 lg:mt-7" : ""}`}
                      key={idx}
                    >
                      <JobAdSummaryCard
                        isActive={selectedJobId === job.id}
                        setSelectedJobId={setSelectedJobId}
                        key={job.id}
                        title={job.title}
                        employer={job.companyName}
                        organization={job.organization.name}
                        visibility={job.visibility}
                        jobAdId={job.id}
                        datePosted={job.createdAt}
                        location={job.location}
                        onShare={handleJobShare}
                      />
                    </div>
                  ))
                ) : (
                  <div className="flex items-center justify-center h-full p-4 lg:p-8 border-[1px] border-[#ACACAC]">
                    <p className="text-[#0E212D] text-lg lg:text-xl">
                      {!!dateFilter || employmentTypesFilter.length > 0
                        ? "No jobs match this filter"
                        : "No jobs available at the moment, please check back later."}
                    </p>
                  </div>
                )}
              </div>

              {selectedJob && (
                <div
                  className={`p-3 w-full border-[#ACACAC] border-[1px] ${
                    !selectedJob ? "hidden" : " block col-span-1 lg:col-span-5"
                  }`}
                >
                  <div className="w-full">
                    <div className="block lg:hidden mb-4">
                      <IconButton
                        aria-label="arrow left"
                        variant={"outline"}
                        rounded={"100%"}
                        borderColor={"#ACACAC"}
                        color={"#0E212D"}
                        onClick={() => {
                          setSelectedJobId(null);
                        }}
                        icon={<Icon as={FaArrowLeft} w={4} h={4} />}
                      />
                    </div>
                    <div className="border-[#ACACAC] border-b-[1px] pb-6 lg:pb-8 flex flex-col lg:flex-row gap-3 lg:justify-between lg:items-end">
                      <div className="w-full flex flex-col gap-3 lg:gap-5">
                        <div className="w-[85%] lg:w-[55%]">
                          <h3 className="text-[#0E212D] text-[18px] lg:text-[23px] font-medium">
                            {selectedJob.title}
                          </h3>
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-[#212121] text-[14px] lg:text-[18px] font-normal">
                            {selectedJob.companyName}
                          </p>
                          <p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
                            Date posted:{" "}
                            <span className="text-[#000000]">
                              {format(new Date(selectedJob.createdAt), "PPP")}
                            </span>
                          </p>
                          {selectedJob.applicationDeadline && (
                            <div className="flex items-center gap-2">
                              <p className="text-[#8d524a] text-[14px] lg:text-[18px]">
                                Application deadline:{" "}
                                <span className="text-[#883227]">
                                  {format(
                                    new Date(selectedJob.applicationDeadline),
                                    "PPP"
                                  )}
                                </span>
                              </p>
                            </div>
                          )}
                          {selectedJob.visibility === "PRIVATE" && (
                            <div className="flex items-center gap-2">
                              <p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
                                Exclusive to{" "}
                                <span className="text-[#000000]">
                                  {selectedJob.organization.name}
                                </span>{" "}
                                members
                              </p>
                              <FaUsers className="text-[#6E6E6E]" size={16} />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col lg:flex-row gap-3">
                        <Button
                          leftIcon={<Icon as={Share} w={4} h={4} />}
                          colorScheme="prussianBlue"
                          variant="outline"
                          h={{
                            base: "40px",
                            lg: "57px",
                          }}
                          fontSize={{
                            base: "16px",
                            lg: "20px",
                          }}
                          onClick={() =>
                            handleJobShare(selectedJob.id, selectedJob.title)
                          }
                        >
                          Share
                        </Button>
                        <Button
                          bgColor={
                            selectedJob.hasApplied && !selectedJob.canApply
                              ? "#FBE2DE"
                              : "#ED6E5C"
                          }
                          color={
                            selectedJob.hasApplied && !selectedJob.canApply
                              ? "#F08677"
                              : "#FEFEFE"
                          }
                          h={{
                            base: "40px",
                            lg: "57px",
                          }}
                          fontSize={{
                            base: "16px",
                            lg: "20px",
                          }}
                          rightIcon={
                            <Icon
                              as={
                                selectedJob.hasApplied && !selectedJob.canApply
                                  ? IoCheckmarkCircle
                                  : FaArrowRight
                              }
                              fill={
                                selectedJob.hasApplied && !selectedJob.canApply
                                  ? "#F08677"
                                  : "#FEFEFE"
                              }
                              w={{
                                base: 4,
                                lg: 5,
                              }}
                              h={{
                                base: 4,
                                lg: 5,
                              }}
                              ml={0}
                              fontWeight={"500"}
                            />
                          }
                          isDisabled={
                            selectedJob.hasApplied && !selectedJob.canApply
                          }
                          onClick={() => {
                            logEvent("job_application_started_from_job_board", {
                              job_id: selectedJob.id,
                              job_title: selectedJob.title,
                            });

                            history.push(
                              `/jobs/${selectedJob.id}/apply${isEmbedded ? "?embed=true" : ""}`
                            );
                          }}
                        >
                          {selectedJob.hasApplied && !selectedJob.canApply
                            ? "Applied"
                            : "Apply"}
                        </Button>
                      </div>
                    </div>
                    <div className="border-[#ACACAC] border-b-[1px] py-6 lg:py-8">
                      <div
                        className="markdown"
                        dangerouslySetInnerHTML={{
                          __html: selectedJob.description,
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-[2fr,2fr] gap-6 lg:gap-8 py-6 lg:py-8">
                      <div className="flex items-center gap-4">
                        <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                          <Icon
                            as={GrLocation}
                            w={{
                              base: "20px",
                              lg: "20px",
                            }}
                            h={{
                              base: "20px",
                              lg: "20px",
                            }}
                            color={"#212121"}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                            Location
                          </p>
                          <p className="text-[#212121] text-[16px] lg:text-[20px]">
                            {`${selectedJob.location.area}, ${selectedJob.location.state}, ${selectedJob.location.country}`}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                          <Icon
                            as={MdOutlineBusinessCenter}
                            w={{
                              base: "20px",
                              lg: "20px",
                            }}
                            h={{
                              base: "20px",
                              lg: "20px",
                            }}
                            color={"#212121"}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                            Employment type
                          </p>
                          <p className="text-[#212121] text-[16px] lg:text-[20px]">
                            {`${employmentTypeToText(
                              selectedJob.employmentType
                            )}`}
                          </p>
                        </div>
                      </div>

                      {selectedJob.duration && (
                        <div className="flex items-center gap-4">
                          <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                            <Icon
                              as={RxCounterClockwiseClock}
                              w={{
                                base: "20px",
                                lg: "20px",
                              }}
                              h={{
                                base: "20px",
                                lg: "20px",
                              }}
                              color={"#212121"}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                              Duration
                            </p>
                            <p className="text-[#212121] text-[16px] lg:text-[20px]">
                              {formatContractJobAdDuration(
                                selectedJob.duration
                              )}
                            </p>
                          </div>
                        </div>
                      )}

                      {selectedJob.salaryDetails && (
                        <div className="flex items-center gap-4">
                          <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                            <Icon
                              as={FaRegMoneyBillAlt}
                              w={{
                                base: "20px",
                                lg: "20px",
                              }}
                              h={{
                                base: "20px",
                                lg: "20px",
                              }}
                              color={"#212121"}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                              {`Salary ${
                                selectedJob.salaryDetails.cap ? "range" : ""
                              }`}
                            </p>
                            <p className="text-[#212121] text-[16px] lg:text-[20px]">
                              {formatAsCurrency(
                                selectedJob.salaryDetails.starting,
                                selectedJob.salaryDetails.currencyCode
                              )}{" "}
                              {selectedJob.salaryDetails.cap
                                ? `- ${formatAsCurrency(
                                    selectedJob.salaryDetails.cap,
                                    selectedJob.salaryDetails.currencyCode
                                  )}`
                                : ""}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-4">
                        <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                          <Icon
                            as={RiBuildingLine}
                            w={{
                              base: "20px",
                              lg: "20px",
                            }}
                            h={{
                              base: "20px",
                              lg: "20px",
                            }}
                            color={"#212121"}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                            Employer
                          </p>
                          <p className="text-[#212121] text-[16px] lg:text-[20px]">
                            {selectedJob.companyName}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                          <Icon
                            as={Tags}
                            w={{
                              base: "20px",
                              lg: "20px",
                            }}
                            h={{
                              base: "20px",
                              lg: "20px",
                            }}
                            color={"#212121"}
                          />
                        </div>

                        {selectedJob.tags.length > 0 ? (
                          <div className="flex flex-col gap-2">
                            <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                              Tags
                            </p>

                            <div className="flex flex-wrap gap-2">
                              {selectedJob.tags.map((tag) => (
                                <Tag
                                  size={"lg"}
                                  key={tag.id}
                                  borderRadius="full"
                                  colorScheme={"lavender"}
                                >
                                  <TagLabel>{tag.name}</TagLabel>
                                </Tag>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                              {selectedJob.categories.length > 1
                                ? "Categories"
                                : "Category"}
                            </p>
                            <p className="text-[#212121] text-[16px] lg:text-[20px]">
                              {selectedJob.categories
                                .map((category) => category.name)
                                .join(", ")}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Desktop pagination */}
            <div className="gap-4 items-center py-4 w-[60%] lg:w-[30%] hidden lg:flex">
              <Button
                backgroundColor={"#FEFEFE"}
                color={"#0E1E27"}
                border={"1px solid #121212"}
                paddingY={{
                  base: 4,
                  lg: 6,
                }}
                w={"40%"}
                fontSize={{
                  base: 15,
                  lg: 18,
                }}
                onClick={handlePrevPage}
                isDisabled={currentPage === 1 || totalPages === 0}
              >
                Prev
              </Button>
              <Button
                backgroundColor={"#FEFEFE"}
                color={"#0E1E27"}
                border={"1px solid #121212"}
                paddingY={{
                  base: 4,
                  lg: 6,
                }}
                w={"40%"}
                fontSize={{
                  base: 15,
                  lg: 18,
                }}
                onClick={handleNextPage}
                isDisabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>

            {/* Mobile pagination hidden when job is in focus */}
            <div
              className={`gap-4 items-center py-4 w-[60%] lg:w-[30%] lg:hidden ${
                !selectedJob ? "flex" : "hidden"
              }`}
            >
              <Button
                backgroundColor={"#FEFEFE"}
                color={"#0E1E27"}
                border={"1px solid #121212"}
                paddingY={{
                  base: 4,
                  lg: 6,
                }}
                w={"40%"}
                fontSize={{
                  base: 15,
                  lg: 18,
                }}
                onClick={handlePrevPage}
                isDisabled={currentPage === 1 || totalPages === 0}
              >
                Prev
              </Button>
              <Button
                backgroundColor={"#FEFEFE"}
                color={"#0E1E27"}
                border={"1px solid #121212"}
                paddingY={{
                  base: 4,
                  lg: 6,
                }}
                w={"40%"}
                fontSize={{
                  base: 15,
                  lg: 18,
                }}
                onClick={handleNextPage}
                isDisabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </section>
      </VStack>
      <Footer />
    </>
  );
}
